/**
 * TODO: Factorize with CAPEX Intelligence ? Almost the same 😕
 */

import * as R from 'ramda';

import { KPI } from './api';
import {
  DataType, FilterType, PerimeterFilter, PerimeterFilterRangeValue, PerimeterFilterValue, PerimeterPredicate,
} from './perimeter-filter';

/**
 * Defines a perimeter filter displayed in the `PerimetersFiltersDialogComponent`.
 */
export class PerimeterReferenceDataFilter extends PerimeterFilter {
  public referenceField: KPI.ReferenceField;

  constructor(referenceField: KPI.ReferenceField, perimeters: KPI.Perimeter[], users?: KPI.User[]) {
    super();

    this.id = 'reference-data';
    this.referenceField = referenceField;
    this.label = this.referenceField.label;

    switch (this.referenceField.data_type) {
      case KPI.ReferenceFieldDataType.DATA_TYPE_FK:
        this.dataType = DataType.DATA_TYPE_FK;
        break;
      case KPI.ReferenceFieldDataType.DATA_TYPE_NUMBER:
        this.dataType = DataType.DATA_TYPE_NUMBER;
        break;
      case KPI.ReferenceFieldDataType.DATA_TYPE_STRING:
        this.dataType = DataType.DATA_TYPE_STRING;
        break;
      case KPI.ReferenceFieldDataType.DATA_TYPE_USER:
        this.dataType = DataType.DATA_TYPE_USER;
        break;
      default:
        this.dataType = null;
        break;
    }

    switch (this.referenceField.filtering_intelligence) {
      case KPI.ReferenceFieldFilterType.FILTER_RANGE:
        this.filtering = FilterType.FILTER_RANGE;
        break;
      case KPI.ReferenceFieldFilterType.FILTER_VALUE:
        this.filtering = FilterType.FILTER_VALUE;
        break;
      default:
        this.filtering = null;
        break;
    }

    // Get all values present on those perimeters for this field
    const refDataElements = perimeters.reduce(
      (elements, perimeter) => [
        ...elements,
        ...PerimeterReferenceDataFilter.getPerimeterReferenceData(perimeter, referenceField.var_name),
      ],
      [],
    );
    const refDataElementsValues = R.reject(
      R.either(Array.isArray, R.isEmpty),
      R.pluck('value', refDataElements),
    ); // Remove empty and multi-valued values coming from summed fields

    if (refDataElementsValues.length > 0) {
      // Compute possible values for the filter
      if (referenceField.filtering_intelligence === KPI.ReferenceFieldFilterType.FILTER_VALUE) {
        switch (referenceField.data_type) {
          // String/number data type: take all possible values
          case KPI.ReferenceFieldDataType.DATA_TYPE_STRING:
          case KPI.ReferenceFieldDataType.DATA_TYPE_NUMBER:
            this.availableValues = R.uniq(refDataElementsValues).sort();
            break;
          // Foreign key data type: take the pre-defined values
          case KPI.ReferenceFieldDataType.DATA_TYPE_FK:
            this.availableValues = this.referenceField.values;
            break;
          // User data type: take users as possible values
          case KPI.ReferenceFieldDataType.DATA_TYPE_USER:
            this.availableValues = users;
            break;
        }
        // Range filtering: take the min and max of the present values
      } else if (referenceField.filtering_intelligence === KPI.ReferenceFieldFilterType.FILTER_RANGE) {
        this.availableValuesRange = PerimeterFilterRangeValue.getRanges(
          Math.min(...refDataElementsValues),
          Math.max(...refDataElementsValues),
          3,
        );
      }
    }
  }

  public getPredicate(values: PerimeterFilterValue[] | PerimeterFilterRangeValue): PerimeterPredicate {
    // Build a predicate matching a perimeter reference value with the provided values
    const referenceDataPredicate = (referenceValue: KPI.PerimeterReferenceValue) => {
      if (this.referenceField.filtering_intelligence === KPI.ReferenceFieldFilterType.FILTER_VALUE) {
        const filterValues = <PerimeterFilterValue[]>values;
        switch (this.referenceField.data_type) {
          case KPI.ReferenceFieldDataType.DATA_TYPE_STRING:
            return R.includes(referenceValue.value, filterValues);
          case KPI.ReferenceFieldDataType.DATA_TYPE_NUMBER:
            return R.includes(referenceValue.value, filterValues.map(Number));
          case KPI.ReferenceFieldDataType.DATA_TYPE_FK:
            return R.includes(referenceValue.filter_value, filterValues.map(Number));
          case KPI.ReferenceFieldDataType.DATA_TYPE_USER:
            return R.includes(referenceValue.filter_value, filterValues.map((user: KPI.User) => user.get_user_id));
        }
      } else if (this.referenceField.filtering_intelligence === KPI.ReferenceFieldFilterType.FILTER_RANGE) {
        const { lower, upper } = <PerimeterFilterRangeValue>values;
        return Number(referenceValue.value) >= lower && Number(referenceValue.value) <= upper;
      }
    };

    // Build a predicate matching a perimeter
    const predicate = (perimeter: KPI.Perimeter) => {
      const currentYear = new Date().getFullYear();
      const perimeterRefData = perimeter._filter_ref_data
        // Get the reference data for this field, less or equal to the current year
        .filter((refData) =>
          refData?.var_name === this.referenceField?.var_name &&
          refData.year <= currentYear,
        )
        // Sort by year in descending order
        .sort((a, b) => b.year - a.year)
      // Take the first one (the closest to the current year)
      [0]
        ;

      return perimeterRefData && referenceDataPredicate(perimeterRefData);
    };

    return predicate;
  }

  private static getPerimeterReferenceData(perimeter: KPI.Perimeter, varName: string): KPI.PerimeterReferenceValue[] {
    return [].concat(
      perimeter.ref_data?.filter((elementReferenceData) => elementReferenceData.var_name === varName),
      ...(perimeter.sub_perimeters ? perimeter.sub_perimeters.map(
        (subPerimeter) => PerimeterReferenceDataFilter.getPerimeterReferenceData(subPerimeter, varName),
      ) : []),
    );
  }
}
